import { Link } from "@/i18n/routing";
import React, { useState, useMemo } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { LinkType } from "@/types/link-type";
import { Tooltip } from "@mui/material";
import { useTranslations } from "next-intl";
import useIsMounted from "@/hooks/useIsMounted";
import useBankRequisitesAvailable from "@/hooks/useBankRequisitesAvailable";

const SublinkedLinkMobile = ({ link, onNavigate }: { link: LinkType; onNavigate?: () => void }) => {
    const translate_page_names = useTranslations('page_names');
    const isMounted = useIsMounted();
    const { isAvailable: bankRequisitesAvailable } = useBankRequisitesAvailable();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    
    // Filter subLinks based on bank requisites availability
    const filteredSubLinks = useMemo(() => {
        if (!link?.subLinks) return [];
        return link.subLinks.filter(sublink => {
            // If sublink requires bank requisites and they're not available, hide it
            if (sublink.requiresBankRequisites && !bankRequisitesAvailable) {
                return false;
            }
            return true;
        });
    }, [link?.subLinks, bankRequisitesAvailable]);

    if (!isMounted) return null;

    const handleSubNavigate = () => {
        onNavigate?.();
    };
    
    // If no sublinks after filtering, don't render
    if (filteredSubLinks.length === 0) return null;
    
    return (
        <li className={cn("flex flex-col group")} key={link.url}>
            <Tooltip
                title={translate_page_names(link.title)}
                placement="right"
            >
                <div
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 hover:bg-white/10 hover:scale-105 active:scale-95 relative overflow-hidden text-white"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-500/0 to-brand-600/0 group-hover:from-brand-500/10 group-hover:to-brand-600/10 transition-all duration-300 rounded-xl"></div>
                    <div className="flex items-center gap-3 relative z-10">
                        <span className="text-slate-300 group-hover:text-white transition-colors duration-200">
                            {link.icon}
                        </span>
                        <span className="font-medium text-slate-300 group-hover:text-white transition-colors duration-200">
                            {translate_page_names(link.title)}
                        </span>
                    </div>
                    <ArrowUp 
                        className={cn(
                            "w-4 h-4 text-chrome-400 group-hover:text-chrome-100 transition-all duration-200 relative z-10", 
                            isDropdownOpen ? "rotate-0" : "rotate-180"
                        )} 
                    />
                </div>
            </Tooltip>

            {isDropdownOpen && (
                <ul className="mt-2 space-y-1 pl-4 border-l-2 border-slate-600/50 ml-6">
                    {filteredSubLinks.map((sublink) => (
                        <li key={sublink.title} className="group/sub">
                            <Tooltip title={translate_page_names(`_${link.title}.${sublink.title}`)} placement="right">
                                <Link
                                    className="flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all duration-200 hover:bg-white/5 hover:translate-x-1 text-chrome-400 hover:text-chrome-100"
                                    href={sublink.url}
                                    onClick={handleSubNavigate}
                                >
                                    <span className="text-xs">{sublink.icon}</span>
                                    <span className="text-sm font-medium">
                                        {translate_page_names(`_${link.title}.${sublink.title}`)}
                                    </span>
                                </Link>
                            </Tooltip>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
};

export default SublinkedLinkMobile;
